/* IntelApps — Header with i18n and theme toggle */
import React, { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useI18n } from '../i18n.js';

const Arrow = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M13 5l7 7-7 7" />
  </svg>
);

function ThemeIcon({ theme }) {
  if (theme === 'dark') {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
      </svg>
    );
  }
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

export default function Header() {
  const {
    theme,
    t,
    changeTheme,
  } = useI18n();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();

  const navItems = [
    { href: '/work', label: t.nav.work, key: 'work' },
    { href: '/services', label: t.nav.services, key: 'services' },
    { href: '/team', label: t.nav.team, key: 'team' },
    { href: '/pricing', label: t.nav.pricing, key: 'pricing' },
  ];

  const isActive = (path) => pathname === path || (path !== '/' && pathname.startsWith(path));

  return (
    <header className="site-header">
      <div className="container nav">
        <Link to="/" className="brand">
          <span className="brand-mark" aria-hidden="true">
            <img src="/assets/Logo.png" alt="" />
          </span>
          IntelApps
        </Link>
        <ul className="nav-links">
          {navItems.map((item) => (
            <li key={item.href}>
              <Link to={item.href} className={isActive(item.href) ? 'active' : ''}>{item.label}</Link>
            </li>
          ))}
        </ul>
        <div className="nav-right">
          <span className="nav-status">{t.common.booking}</span>
          <Link to="/discuss-project" className="btn btn-primary btn-arrow">
            {t.nav.book} <Arrow size={14} />
          </Link>
          <button
            className="lg:hidden theme-toggle"
            type="button"
            aria-label="Menu"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {mobileOpen ? <path d="M6 18L18 6M6 6l12 12" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
            </svg>
          </button>
        </div>
      </div>
      <button
        className="theme-toggle theme-toggle--corner"
        type="button"
        aria-label="Toggle theme"
        onClick={() => changeTheme(theme === 'dark' ? 'light' : 'dark')}
      >
        <ThemeIcon theme={theme} />
      </button>
      {mobileOpen && (
        <div className="lg:hidden bg-[var(--bg)] border-t border-[var(--line-2)]">
          <ul className="flex flex-col px-6 py-4 gap-2">
            {navItems.map((item) => (
              <li key={item.href}>
                <Link to={item.href} className={`block py-2 px-3 rounded-full text-sm font-medium ${isActive(item.href) ? 'text-[var(--ink)] bg-[var(--bg-soft)]' : 'text-[var(--ink-3)]'}`}>
                  {item.label}
                </Link>
              </li>
            ))}
            <li className="pt-2">
              <Link to="/discuss-project" className="btn btn-primary w-full justify-center">
                {t.nav.book} <Arrow size={14} />
              </Link>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}

Arrow.propTypes = {
  // eslint-disable-next-line react/require-default-props
  size: PropTypes.number,
};

ThemeIcon.propTypes = {
  theme: PropTypes.string.isRequired,
};

export { Arrow };
